import React, {ReactElement, useEffect, useState} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import {cx} from '@emotion/css';

import CurrentUser from '@fo/components/Identity/CurrentUser/CurrentUser';
import {getPageRoute} from '@fo/utils/routes/getPageRoute';
import {ArrowIcon} from '@fo/root/designSystem/atoms/Icons';

import {ReactComponent as AccountIcon} from '@fo/shared-images/icons/account.svg';
import {ReactComponent as ClipboardIcon} from '@fo/shared-images/icons/clipboard.svg';
import {ReactComponent as LogoutIcon} from '@fo/shared-images/icons/logout.svg';

import {ButtonVariant} from '@fo/designSystem/Buttons/Button/types';
import Button from '@fo/designSystem/Buttons/Button/Button';
import useIdentityModal from '@fo/components/Identity/useIdentityModal';
import {IconRotation, IconSize} from '@fo/root/designSystem/atoms/Icons/types';
import {useIdentityContext} from '@fo/shared-providers/IdentityContext';
import {TSectionWithLabel} from '@fo/designSystem/Navigation/types';
import {segmentEvents, track} from '@fo/shared-analytics/tracking/segment';

import styles from './NavigationUserMenuMobile.styles';

interface INavigationUserMenu {
  loginMenuText: string;
  onMenuOpen(): void;
  openSectionInParent?: TSectionWithLabel;
}

function NavigationUserMenuMobile({openSectionInParent, loginMenuText, onMenuOpen}: INavigationUserMenu): ReactElement {
  const {isLoggedIn, logout} = useIdentityContext();
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const {formatMessage} = useIntl();
  const {showLoginModal} = useIdentityModal();

  useEffect(() => {
    if (openSectionInParent) {
      setIsMenuOpen(false);
    }
  }, [openSectionInParent]);

  return isLoggedIn ? (
    <>
      <Button
        onClick={() => {
          onMenuOpen();
          setIsMenuOpen((currentState) => !currentState);
          track(segmentEvents.navigation.loginButton);
        }}
        variant={ButtonVariant.UNSTYLED}
        ariaLabel={loginMenuText}
        testId="button.navigation.userMenu.mainButton"
        className={cx(styles.menuButton)}
      >
        <CurrentUser className={cx(styles.userName)} />
        <ArrowIcon rotation={isMenuOpen ? IconRotation.CCW_90 : IconRotation.CW_90} size={IconSize.MEDIUM} />
      </Button>
      <div className={cx(styles.linksContainer(isMenuOpen))}>
        <Button
          variant={ButtonVariant.UNSTYLED}
          ariaLabel={formatMessage({id: 'phoenix.nav.dashboard.linkText'})}
          className={cx(styles.linkButton)}
          href={getPageRoute('dashboard.home')}
        >
          <ClipboardIcon className={cx(styles.icon)} />
          <span className={cx(styles.text)}>
            <FormattedMessage id="phoenix.nav.dashboard.linkText" />
          </span>
        </Button>
        <Button
          variant={ButtonVariant.UNSTYLED}
          ariaLabel={formatMessage({id: 'phoenix.nav.dashboard.linkText'})}
          className={cx(styles.linkButton)}
          href={getPageRoute('dashboard.accountDetails')}
        >
          <AccountIcon className={cx(styles.icon)} />
          <span className={cx(styles.text)}>
            <FormattedMessage id="phoenix.nav.accountDetails.linkText" />
          </span>
        </Button>
        <Button
          ariaLabel={formatMessage({id: 'phoenix.logout.button.text'})}
          id="logout"
          type="button"
          onClick={() => logout()}
          variant={ButtonVariant.UNSTYLED}
          className={cx(styles.linkButton)}
        >
          <LogoutIcon className={cx(styles.icon)} />
          <span className={cx(styles.text)}>
            <FormattedMessage id="phoenix.logout.button.text" />
          </span>
        </Button>
      </div>
    </>
  ) : (
    <Button
      onClick={showLoginModal}
      variant={ButtonVariant.UNSTYLED}
      ariaLabel={loginMenuText}
      testId="button.navigation.login.mobile"
      className={cx(styles.loginButton)}
    >
      <span className={cx(styles.menuButtonText)}>{loginMenuText}</span>
      <AccountIcon className={cx(styles.icon)} />
    </Button>
  );
}

export default NavigationUserMenuMobile;
